import "./schedule-exceptions-manager.scss";

import {
  CreateScheduleExceptionDto,
  CreateScheduleExceptionDtoExceptionTypeEnum,
  GetScheduleExceptionDto,
} from "@api/api";
import {
  useQyCreateScheduleException,
  useQyDeleteScheduleException,
  useQyGetUpcomingExceptions,
  useQyUpdateScheduleException,
} from "@factory/query/schedule-exception.query";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { RadioButton } from "primereact/radiobutton";
import { useCallback, useEffect, useMemo, useState } from "react";

interface ScheduleExceptionsManagerProps {
  tenant: string;
}

type DateSelectionMode = "single" | "multiple" | "range";

interface ExceptionFormData {
  code?: string;
  dateSelectionMode: DateSelectionMode;
  exceptionDate: Date | null;
  exceptionDates: Date[];
  dateRange: Date[];
  exceptionType: CreateScheduleExceptionDtoExceptionTypeEnum;
  title: string;
  description: string;
  isFullDayClosure: boolean;
  openTime: Date | null;
  closeTime: Date | null;
  recurringYearly: boolean;
}

const EXCEPTION_TYPES = [
  {
    value: CreateScheduleExceptionDtoExceptionTypeEnum.Holiday,
    label: "Holiday",
    icon: "pi pi-gift",
  },
  {
    value: CreateScheduleExceptionDtoExceptionTypeEnum.Closure,
    label: "Closure",
    icon: "pi pi-times-circle",
  },
  {
    value: CreateScheduleExceptionDtoExceptionTypeEnum.SpecialHours,
    label: "Special Hours",
    icon: "pi pi-clock",
  },
];

const getDefaultFormData = (): ExceptionFormData => ({
  dateSelectionMode: "single",
  exceptionDate: null,
  exceptionDates: [],
  dateRange: [],
  exceptionType: CreateScheduleExceptionDtoExceptionTypeEnum.Holiday,
  title: "",
  description: "",
  isFullDayClosure: true,
  openTime: null,
  closeTime: null,
  recurringYearly: false,
});

const parseTimeString = (timeStr: string | undefined): Date | null => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const formatTimeToString = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const formatDateToYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (
  dateStr: string,
): { month: string; day: string; year: string } => {
  const date = new Date(dateStr);
  return {
    month: date.toLocaleDateString("en-US", { month: "short" }),
    day: date.getDate().toString(),
    year: date.getFullYear().toString(),
  };
};

const isPastDate = (dateStr: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
};

export function ScheduleExceptionsManager({
  tenant,
}: ScheduleExceptionsManagerProps) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] =
    useState<ExceptionFormData>(getDefaultFormData());
  const [isEditing, setIsEditing] = useState(false);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [first, setFirst] = useState(0);
  const rowsPerPage = 10;

  const { data: exceptions, isLoading } = useQyGetUpcomingExceptions(
    tenant,
    999,
    true,
  );

  const { mutate: createException, isLoading: isCreating } =
    useQyCreateScheduleException(() => {
      setShowModal(false);
      resetForm();
    });

  const { mutate: updateException, isLoading: isUpdating } =
    useQyUpdateScheduleException(() => {
      setShowModal(false);
      resetForm();
    });

  const { mutate: deleteException, isLoading: isDeleting } =
    useQyDeleteScheduleException();

  const filteredExceptions = useMemo(() => {
    if (!exceptions) return [];
    if (!filterType) return exceptions;
    return exceptions.filter((e) => e.exception_type === filterType);
  }, [exceptions, filterType]);

  const paginatedExceptions = useMemo(() => {
    return filteredExceptions.slice(first, first + rowsPerPage);
  }, [filteredExceptions, first]);

  const onPageChange = useCallback((event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
  }, []);

  // Reset pagination when filter changes
  useEffect(() => {
    setFirst(0);
  }, [filterType]);

  const resetForm = useCallback(() => {
    setFormData(getDefaultFormData());
    setIsEditing(false);
  }, []);

  const handleOpenModal = useCallback(
    (exception?: GetScheduleExceptionDto) => {
      if (exception) {
        setFormData({
          code: exception.code,
          dateSelectionMode: "single",
          exceptionDate: new Date(exception.exception_date),
          exceptionDates: [],
          dateRange: [],
          exceptionType:
            exception.exception_type as CreateScheduleExceptionDtoExceptionTypeEnum,
          title: exception.title,
          description: exception.description || "",
          isFullDayClosure: Boolean(exception.is_full_day_closure ?? true),
          openTime: parseTimeString(exception.open_time),
          closeTime: parseTimeString(exception.close_time),
          recurringYearly: Boolean(exception.recurring_yearly ?? false),
        });
        setIsEditing(true);
      } else {
        resetForm();
      }
      setShowModal(true);
    },
    [resetForm],
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    resetForm();
  }, [resetForm]);

  const handleFormChange = useCallback(
    <K extends keyof ExceptionFormData>(
      field: K,
      value: ExceptionFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const hasValidDateSelection = useMemo(() => {
    if (isEditing) {
      return Boolean(formData.exceptionDate);
    }

    switch (formData.dateSelectionMode) {
      case "single":
        return Boolean(formData.exceptionDate);
      case "multiple":
        return formData.exceptionDates.length > 0;
      case "range":
        return (
          formData.dateRange.length === 2 &&
          Boolean(formData.dateRange[0]) &&
          Boolean(formData.dateRange[1])
        );
      default:
        return false;
    }
  }, [formData, isEditing]);

  const handleDateSelectionModeChange = useCallback(
    (mode: DateSelectionMode) => {
      setFormData((prev) => ({
        ...prev,
        dateSelectionMode: mode,
        exceptionDate: null,
        exceptionDates: [],
        dateRange: [],
      }));
    },
    [],
  );

  const handleSave = () => {
    if (!hasValidDateSelection || !formData.title) {
      return;
    }

    const payload: CreateScheduleExceptionDto = {
      tenant,
      exception_type: formData.exceptionType,
      title: formData.title,
      description: formData.description || undefined,
      is_full_day_closure: formData.isFullDayClosure,
      open_time: !formData.isFullDayClosure
        ? formatTimeToString(formData.openTime)
        : undefined,
      close_time: !formData.isFullDayClosure
        ? formatTimeToString(formData.closeTime)
        : undefined,
      recurring_yearly: formData.recurringYearly,
    };

    if (isEditing && formData.code) {
      if (!formData.exceptionDate) {
        return;
      }

      updateException({
        ...payload,
        code: formData.code,
        exception_date: formatDateToYmd(formData.exceptionDate),
      });
    } else {
      if (formData.dateSelectionMode === "single" && formData.exceptionDate) {
        payload.exception_date = formatDateToYmd(formData.exceptionDate);
      }

      if (formData.dateSelectionMode === "multiple") {
        payload.exception_dates = formData.exceptionDates.map(formatDateToYmd);
      }

      if (formData.dateSelectionMode === "range") {
        const [rangeStart, rangeEnd] = formData.dateRange;

        if (!rangeStart || !rangeEnd) {
          return;
        }

        payload.date_range_start = formatDateToYmd(rangeStart);
        payload.date_range_end = formatDateToYmd(rangeEnd);
      }

      createException(payload);
    }
  };

  const handleDelete = (exception: GetScheduleExceptionDto) => {
    confirmDialog({
      message: `Are you sure you want to delete "${exception.title}"?`,
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        deleteException({ code: exception.code });
      },
    });
  };

  const getExceptionTypeClass = (type: string): string => {
    switch (type) {
      case "HOLIDAY":
        return "holiday";
      case "CLOSURE":
        return "closure";
      case "SPECIAL_HOURS":
        return "special-hours";
      default:
        return "";
    }
  };

  const getExceptionTypeLabel = (type: string): string => {
    const found = EXCEPTION_TYPES.find((t) => t.value === type);
    return found?.label || type;
  };

  if (isLoading) {
    return (
      <div className="schedule-exceptions-manager">
        <div className="loading-overlay">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span className="loading-text">Loading exceptions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-exceptions-manager">
      <ConfirmDialog />

      <div className="exceptions-header">
        <h2>Schedule Exceptions</h2>
        <Button
          label="Add Exception"
          icon="pi pi-plus"
          onClick={() => handleOpenModal()}
        />
      </div>

      <div className="filter-section">
        <div className="filter-item">
          <label>Filter by type:</label>
          <Dropdown
            value={filterType}
            options={[
              { label: "All Types", value: null },
              ...EXCEPTION_TYPES.map((t) => ({
                label: t.label,
                value: t.value,
              })),
            ]}
            onChange={(e) => setFilterType(e.value)}
            placeholder="All Types"
            className="w-48"
          />
        </div>
      </div>

      {filteredExceptions.length === 0 ? (
        <div className="empty-state">
          <i className="pi pi-calendar-times"></i>
          <h3>No Exceptions Found</h3>
          <p>
            {filterType
              ? "No exceptions match the selected filter."
              : "Add holidays, closures, or special hours to your schedule."}
          </p>
        </div>
      ) : (
        <>
          <div className="exceptions-list">
            {paginatedExceptions.map((exception) => {
              const dateInfo = formatDate(exception.exception_date);
              const isPast = isPastDate(exception.exception_date);
              const typeClass = getExceptionTypeClass(exception.exception_type);

              return (
                <div
                  key={exception.code}
                  className={`exception-card ${typeClass} ${isPast ? "past" : ""}`}
                >
                  <div className="date-section">
                    <span className="date-month">{dateInfo.month}</span>
                    <span className="date-day">{dateInfo.day}</span>
                  </div>

                  <div className="details-section">
                    <div className="exception-header">
                      <span className="exception-title">{exception.title}</span>
                      <span className={`exception-type ${typeClass}`}>
                        {getExceptionTypeLabel(exception.exception_type)}
                      </span>
                      {exception.recurring_yearly && (
                        <span className="recurring-badge">
                          <i className="pi pi-sync"></i>
                        </span>
                      )}
                    </div>

                    <div className="exception-meta">
                      <span className="date-year">{dateInfo.year}</span>
                      {!exception.is_full_day_closure && (
                        <span className="time-info">
                          <i className="pi pi-clock"></i>
                          {exception.open_time} - {exception.close_time}
                        </span>
                      )}
                      {exception.description && (
                        <span className="exception-description">
                          {exception.description}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="actions-section">
                    <Button
                      icon="pi pi-pencil"
                      className="p-button-text p-button-sm"
                      onClick={() => handleOpenModal(exception)}
                      disabled={isPast}
                      tooltip={isPast ? "Cannot edit past exceptions" : "Edit"}
                      tooltipOptions={{ position: "top" }}
                    />
                    <Button
                      icon="pi pi-trash"
                      className="p-button-text p-button-danger p-button-sm"
                      onClick={() => handleDelete(exception)}
                      loading={isDeleting}
                      tooltip="Delete"
                      tooltipOptions={{ position: "top" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {filteredExceptions.length > rowsPerPage && (
            <Paginator
              first={first}
              rows={rowsPerPage}
              totalRecords={filteredExceptions.length}
              onPageChange={onPageChange}
              className="exceptions-paginator"
            />
          )}
        </>
      )}

      <Dialog
        header={isEditing ? "Edit Exception" : "Add Exception"}
        visible={showModal}
        onHide={handleCloseModal}
        className="exception-modal"
        style={{ width: "500px" }}
        modal
        draggable={false}
      >
        <div className="form-grid">
          {!isEditing && (
            <div className="form-field">
              <label>
                Date Selection <span className="required">*</span>
              </label>
              <div className="date-mode-options">
                <div className="type-option">
                  <RadioButton
                    inputId="date-mode-single"
                    value="single"
                    checked={formData.dateSelectionMode === "single"}
                    onChange={(e) =>
                      handleDateSelectionModeChange(
                        e.value as DateSelectionMode,
                      )
                    }
                  />
                  <label htmlFor="date-mode-single">Single date</label>
                </div>
                <div className="type-option">
                  <RadioButton
                    inputId="date-mode-multiple"
                    value="multiple"
                    checked={formData.dateSelectionMode === "multiple"}
                    onChange={(e) =>
                      handleDateSelectionModeChange(
                        e.value as DateSelectionMode,
                      )
                    }
                  />
                  <label htmlFor="date-mode-multiple">Multiple dates</label>
                </div>
                <div className="type-option">
                  <RadioButton
                    inputId="date-mode-range"
                    value="range"
                    checked={formData.dateSelectionMode === "range"}
                    onChange={(e) =>
                      handleDateSelectionModeChange(
                        e.value as DateSelectionMode,
                      )
                    }
                  />
                  <label htmlFor="date-mode-range">Date range</label>
                </div>
              </div>
            </div>
          )}

          <div className="form-field">
            <label>
              {isEditing
                ? "Date"
                : formData.dateSelectionMode === "multiple"
                  ? "Dates"
                  : formData.dateSelectionMode === "range"
                    ? "Date Range"
                    : "Date"}{" "}
              <span className="required">*</span>
            </label>
            {(isEditing || formData.dateSelectionMode === "single") && (
              <Calendar
                value={formData.exceptionDate}
                onChange={(e) =>
                  handleFormChange("exceptionDate", e.value ?? null)
                }
                minDate={new Date()}
                showIcon
                dateFormat="MM dd, yy"
                placeholder="Select date"
                className="w-full"
              />
            )}

            {!isEditing && formData.dateSelectionMode === "multiple" && (
              <Calendar
                value={formData.exceptionDates}
                onChange={(e) =>
                  handleFormChange("exceptionDates", (e.value as Date[]) || [])
                }
                minDate={new Date()}
                showIcon
                selectionMode="multiple"
                dateFormat="MM dd, yy"
                placeholder="Select one or more dates"
                className="w-full"
                readOnlyInput
              />
            )}

            {!isEditing && formData.dateSelectionMode === "range" && (
              <Calendar
                value={formData.dateRange}
                onChange={(e) =>
                  handleFormChange("dateRange", (e.value as Date[]) || [])
                }
                minDate={new Date()}
                showIcon
                selectionMode="range"
                dateFormat="MM dd, yy"
                placeholder="Select start and end date"
                className="w-full"
                readOnlyInput
              />
            )}
          </div>

          <div className="form-field">
            <label>
              Type <span className="required">*</span>
            </label>
            <div className="exception-type-options">
              {EXCEPTION_TYPES.map((type) => (
                <div key={type.value} className="type-option">
                  <RadioButton
                    inputId={type.value}
                    value={type.value}
                    checked={formData.exceptionType === type.value}
                    onChange={(e) =>
                      handleFormChange(
                        "exceptionType",
                        e.value as CreateScheduleExceptionDtoExceptionTypeEnum,
                      )
                    }
                  />
                  <label htmlFor={type.value}>
                    <i className={type.icon}></i> {type.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label>
              Title <span className="required">*</span>
            </label>
            <InputText
              value={formData.title}
              onChange={(e) => handleFormChange("title", e.target.value)}
              placeholder="e.g., Christmas Day, Staff Training"
              className="w-full"
            />
          </div>

          <div className="form-field">
            <label>Description</label>
            <InputTextarea
              value={formData.description}
              onChange={(e) => handleFormChange("description", e.target.value)}
              placeholder="Additional details (optional)"
              rows={2}
              className="w-full"
              autoResize
            />
          </div>

          {formData.exceptionType ===
            CreateScheduleExceptionDtoExceptionTypeEnum.SpecialHours && (
            <>
              <div className="form-field">
                <label className="flex items-center gap-2">
                  <InputSwitch
                    checked={formData.isFullDayClosure}
                    onChange={(e) =>
                      handleFormChange("isFullDayClosure", Boolean(e.value))
                    }
                  />
                  Closed all day
                </label>
              </div>

              {!formData.isFullDayClosure && (
                <div className="time-fields">
                  <div className="form-field">
                    <label>Open Time</label>
                    <Calendar
                      value={formData.openTime}
                      onChange={(e) =>
                        handleFormChange("openTime", e.value ?? null)
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={15}
                      showIcon
                      icon="pi pi-clock"
                    />
                  </div>
                  <div className="form-field">
                    <label>Close Time</label>
                    <Calendar
                      value={formData.closeTime}
                      onChange={(e) =>
                        handleFormChange("closeTime", e.value ?? null)
                      }
                      timeOnly
                      hourFormat="12"
                      stepMinute={15}
                      showIcon
                      icon="pi pi-clock"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-field">
            <label className="flex items-center gap-2">
              <InputSwitch
                checked={formData.recurringYearly}
                onChange={(e) =>
                  handleFormChange("recurringYearly", Boolean(e.value))
                }
              />
              Repeat every year
            </label>
            <span className="help-text">
              This exception will automatically repeat on the same date every
              year
            </span>
          </div>
        </div>

        <div className="modal-actions">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={handleCloseModal}
          />
          <Button
            label={isEditing ? "Update" : "Create"}
            onClick={handleSave}
            loading={isCreating || isUpdating}
            disabled={!hasValidDateSelection || !formData.title}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default ScheduleExceptionsManager;
